import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { LoaderPropagate } from 'components';
import { Col, Container, Row, Section } from 'components/GridV2';
import { LocaleContext } from 'components/rebrand/Layout';

import { IArticleSanityData, IQueryParamsGROQ } from 'typescriptDefs/pages/learn/article';

import sanityClient from 'helpers/sanityClient';
import { useScrollDepth, useTrackScrollDepth } from 'helpers/useScrollDepth';

import { ArticleFooter } from './ArticleFooter';
import { ArticleHeader } from './ArticleHeader';
import { ContentsSidebar } from './ContentsSidebar';
import { BlockContent } from './content/BlockContent';

type Props = {
  article: IArticleSanityData;
  articles: IArticleSanityData[];
  blogArticleDisclaimer?: string[];
  hasScrolled: boolean;
  isGROQ: boolean;
  pathname: string;
  queryParamsGROQ: IQueryParamsGROQ;
  setArticleInView: Dispatch<SetStateAction<IArticleSanityData>>;
  setArticles: Dispatch<SetStateAction<IArticleSanityData[]>>;
  setQueryParamsGROQ: Dispatch<SetStateAction<IQueryParamsGROQ>>;
  scrollY: number;
};

const Article = ({
  article,
  articles,
  blogArticleDisclaimer,
  hasScrolled,
  isGROQ,
  pathname,
  queryParamsGROQ,
  setArticleInView,
  setArticles,
  setQueryParamsGROQ,
  scrollY,
}: Props) => {
  const locale = useContext(LocaleContext);
  const [refSetCurrent, setCurrentInView] = useInView({ threshold: 0 });
  const [refLoadNext, loadNextInView] = useInView({ threshold: 0, triggerOnce: true });
  useEffect(() => {
    if (setCurrentInView) setArticleInView(article);
  }, [setCurrentInView]);

  const { isLoading } = useFetchArticle({
    queryParamsGROQ,
    setArticles,
    setQueryParamsGROQ,
    shouldFetch: loadNextInView && hasScrolled,
  });

  const { headings, refArticleBody } = useSetHeadings();

  const { refScrollDepthTracking } = useHandleScroll({ article, articles, setArticles, scrollY });

  return (
    <div ref={refLoadNext}>
      <div ref={refSetCurrent}>
        <div ref={refScrollDepthTracking}>
          <ArticleHeader article={article} isGROQ={isGROQ} />

          <Container className="grid grid-cols-1 gap-64 px-32 xs:px-0 md:grid-cols-3">
            <ContentsSidebar article={article} headings={headings} pathname={pathname} />
            <div className="col-span-2">
              {article.summary?.length > 0 && (
                <div className="relative mb-40 rounded-16 bg-primary-50 p-16 xs:p-32">
                  <h2 className="mb-20 text-4xl font-bold leading-normal text-primary-350">
                    {{ en: `Rounding it up`, fr: `En résumé` }[locale]}
                  </h2>
                  <ul className="flex flex-col gap-20">
                    {article.summary.map((bullet) => (
                      <li key={bullet}>
                        <p className="text-lg leading-normal text-grey-400">{bullet}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div ref={refArticleBody}>
                <BlockContent content={isGROQ ? article.body : article._rawBody} className="mb-32" />
              </div>

              <ArticleFooter article={article} blogArticleDisclaimer={blogArticleDisclaimer} isGROQ={isGROQ} />
            </div>
          </Container>
          <Container className="px-32 xs:px-0">
            <div className="h-3 w-full bg-primary-50" />
          </Container>

          {isLoading && (
            <Section className="py-32">
              <Container className="px-32 xs:px-0">
                <Row className="flex-wrap">
                  <Col className="flex w-full flex-none justify-center">
                    <LoaderPropagate />
                  </Col>
                </Row>
              </Container>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Article;

const queryGROQ = `*[!(_id in $idsPublished) && !(_id in $idsDraft) && !(title in $titles) && _type == $_type && locale == $locale && $tags[0] in tags]{
  _createdAt,
  _id,
  _updatedAt,
  "authorData": *[_id == ^.author._ref]{
    _id,
    bio,
    "img": *[_id == ^.img.asset._ref][0]{
      url
    },
    name,
    slug
  }[0],
  body,
  canonicalLink,
  "img": *[_id == ^.img.asset._ref]{
    url
  }[0],
  imgAltText,
  metadataDescription,
  metadataKeywords,
  metadataTitle,
  minutesToRead,
  "reviewerData": *[_id == ^.reviewer._ref]{
    _id,
    "img": *[_id == ^.img.asset._ref][0]{
      url
    },
    name,
    slug
  }[0],
  sources,
  slug,
  subfolder,
  summary,
  tags,
  title,
} | order(dateTimePublished desc)[0]`;

const useFetchArticle = ({
  queryParamsGROQ,
  setArticles,
  setQueryParamsGROQ,
  shouldFetch,
}: {
  queryParamsGROQ: IQueryParamsGROQ;
  setArticles: Dispatch<SetStateAction<IArticleSanityData[]>>;
  setQueryParamsGROQ: Dispatch<SetStateAction<IQueryParamsGROQ>>;
  shouldFetch: boolean;
}) => {
  const locale = useContext(LocaleContext);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (shouldFetch) {
      const fetchArticle = async () => {
        setIsLoading(true);
        const article = await sanityClient.fetch(queryGROQ, queryParamsGROQ);
        setIsLoading(false);
        if (article) {
          setArticles((prevState) => [...prevState, article]);
          setQueryParamsGROQ((prevState) => ({
            _type: prevState._type,
            idsDraft: [...prevState.idsDraft, `drafts.${article._id.replace(/^drafts\./, '')}`],
            idsPublished: [...prevState.idsPublished, article._id.replace(/^drafts\./, '')],
            locale,
            tags: article.tags,
            titles: [...prevState.titles, article.title],
          }));
        }
      };
      fetchArticle();
    }
  }, [shouldFetch]);

  return {
    isLoading,
  };
};

const useSetHeadings = () => {
  const refArticleBody = useRef<HTMLDivElement>(null);
  const [headings, setHeadings] = useState<{ text: string }[]>([]);

  useEffect(() => {
    if (refArticleBody?.current) {
      let headingTagsH3: HTMLHeadingElement[] = [];

      const headingTagsH2 = Array.from(refArticleBody.current.querySelectorAll('h2'));

      if (headingTagsH2.length === 0) {
        headingTagsH3 = Array.from(refArticleBody.current.querySelectorAll('h3'));
      }

      const headingTags = [...headingTagsH2, ...headingTagsH3];

      const headings = headingTags.map((headingTag) => ({
        text: headingTag.innerText ?? headingTag.innerHTML,
      }));

      setHeadings(headings);
    }
  }, [refArticleBody.current]);

  return {
    refArticleBody,
    headings,
  };
};

const useHandleScroll = ({
  article,
  articles,
  scrollY,
  setArticles,
}: {
  article: IArticleSanityData;
  articles: IArticleSanityData[];
  scrollY: number;
  setArticles: Dispatch<SetStateAction<IArticleSanityData[]>>;
}) => {
  const locale = useContext(LocaleContext);
  const refScrollDepthTracking = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Adds how tall the article document is to the schema
    if (refScrollDepthTracking.current) {
      const refArticleClientRect = refScrollDepthTracking.current.getBoundingClientRect();
      const offsetHeight = refArticleClientRect.height;
      setArticles((articles) => {
        articles.forEach((articleItem) => {
          if (articleItem._id === article._id) {
            articleItem.offsetHeight = offsetHeight;
          }
        });
        return articles;
      });
    }
  }, [scrollY, refScrollDepthTracking]);

  const [bodyHeightUpToCurrentArticle, setBodyHeightUpToCurrentArticle] = useState<number>(0);

  useEffect(() => {
    // Obtains how much height the body has up to the current article
    const bodyHeightUpToCurrentArticleCalculated = articles
      .filter((_, i) => i < articles.indexOf(article))
      .reduce((acc, cv) => {
        if (cv.offsetHeight) return acc + cv.offsetHeight;
        return acc;
      }, 0);
    setBodyHeightUpToCurrentArticle(bodyHeightUpToCurrentArticleCalculated);
  }, [articles]);

  const scrollDepth = useScrollDepth({
    elementHeight: article.offsetHeight,
    offsetUpToElement: bodyHeightUpToCurrentArticle,
    scrollY,
  });

  useTrackScrollDepth({ locale, pageName: article.title, scrollDepth });

  return {
    refScrollDepthTracking,
  };
};
